import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Machine } from "../GameComponents/Machine";
import { AppContext } from "../../Common/AppContext";
import { GameData } from "../Channels/Common/Datas/GameData";
import { DataManager } from "../Managers/DataManager";
import { IReel } from "neo-games/Engine/Communication/Game/Types/IReel";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import * as Communication from "neo-games/Engine/Communication/index";
import { PlayList } from "../Components/Sounds/PlayList";
import { BaseView } from "../GameComponents/Base/BaseView";
import { Symbol } from "../GameComponents/Symbol";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";

/**
 * Machine handle reels and symbols for reels rotation.
 * Machine define behaviour for complete flow of reel rotation.
 * @class Machine
 * @extends BaseElement
 */

@engineComponent(ComponentTypes.DisplayObject)
export class MachineExtended extends Machine {
    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    public static SyncReelShowed: string = "eventSyncReelShowed";

    public static SyncReelForceShowed: string = "eventSyncReelForceShowed";

    //******************************************************************************************************************
    //  TIMERS
    //******************************************************************************************************************

    private static ReelStopDelay: number = 18;

    private static ShowSyncedReelTimerDelay: number = 120;

    private static ShowFinalSyncedReelTimerDelay: number = 180;

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    private _syncedReelIndexes: Array<number>;

    private _syncedReelIndexesOrdered: Array<number>;

    private _currentSyncedReelIndex: number;

    private _showSyncedReelTimer: FrameTimerEventEmitter;

    private _forced: boolean;

    private _syncedLights: Array<SwitchElement>;

    private _syncedStrips: Object;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this._showSyncedReelTimer = CreateFrameTimer("showSyncedReelTimer");
        this._showSyncedReelTimer.on(FrameTimerEvents.Complete, this.showSyncedReel.bind(this));
    }

    //******************************************************************************************************************
    //  INITIALIZE
    //******************************************************************************************************************

    public initializeSyncedReelsBg(syncedReelsBg: ContainerElement, reelsData: Array<Communication.Game.IReel>): void {
        if (syncedReelsBg && reelsData) {
            this._syncedLights = [];
            this._syncedLights.push(syncedReelsBg.childs["LightsLeft"]);
            this._syncedLights.push(syncedReelsBg.childs["LightsRight"]);

            for (let i = 0; i < this._reelCount; i++) {
                this._reels[i].setSyncedReelBg(syncedReelsBg.childs["SyncedReel" + i], reelsData[i].customData);
            }
        }
    }

    public initSyncedReelsStrip(syncedReelStrips: Array<{ mask: number; symbols: Array<number> }>): void {
        if (syncedReelStrips) {
            this._syncedStrips = {};

            for (let i = 0; i < syncedReelStrips.length; i++) {
                this._syncedStrips[syncedReelStrips[i].mask] = syncedReelStrips[i].symbols;
            }
        }
    }

    public preInitOffScreenSymbols(): void {
        for (let i = 0; i < this._symbols.symbols.length; i++) {
            for (let j = 0; j < 6; j++) {
                const symbol = this._symbols.symbols[i].clone();
                symbol.name = "Symbol" + i;
                symbol.id = i;

                this._symbols.returnSymbol(symbol);
            }
        }
    }

    //******************************************************************************************************************
    //  OVERRIDES
    //******************************************************************************************************************

    public spinStart(
        longRotation?: boolean,
        useTransform: boolean = false,
        currentSymbols?: Array<Array<Symbol>>,
    ): void {
        if (DEBUG) {
            this.log("spinStart");
        }

        this.resetSyncedReels();
        this.visible = true;
        this._reelsContainer.visible = true;

        this.actualReelStopped = -1;
        this._stoppedReelIndexes = 0;

        this._stoppedFromServer = false;
        this._stoppedFromMachine = false;
        this._stoppedFromLegal = false;

        this._startSpinTime = Date.now();

        if (this._reels != null) {
            for (let i = 0; i < this._reelCount; i++) {
                if (this._reels[i] != null) {
                    if (this._stoppedSymbolsArray.length > 0) {
                        this._reels[i].visible = true;
                        this._reels[i].setData(this._stoppedSymbolsArray[i]);

                        //move current symbols from WinAnimator to Reel, not array reference
                        this._reels[i].currentSymbols = [];
                        for (let j = 0; j < this._visibleSymbolCount; j++) {
                            this._reels[i].currentSymbols.push(currentSymbols[i][j]);
                            currentSymbols[i][j] = null;
                        }

                        this._reels[i].showSymbols(useTransform);
                        this._reels[i].spinStart();
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no stopped symbols");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no reel with index: " + i);
                    }
                }
            }

            if (longRotation != null && longRotation) {
                this._stopTimer.start(this._minSpinRotationLong);
            } else {
                this._stopTimer.start(this._minSpinRotation);
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no reels");
            }
        }

        PlayList.play("SND_BaseRotationStart");
        PlayList.loop("SND_BaseRotation", 0, true);
    }

    public spinStop(event: any, force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        if (fromServer) {
            this._stoppedFromServer = true;
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            if (this.verifyMinGameRoundDurationConditions()) {
                this._legalStopTimer = setTimeout(
                    () => this.spinStop(null, false, false, true),
                    this.getLegalRemainingSpinDuration(),
                );
            } else {
                this._stoppedFromLegal = true;
            }
        } else if (legal) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
        } else {
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (force) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (DEBUG) {
            this.log("spinStop " + force);
        }

        if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
            if (!(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative")) {
                DataManager.updateDataElement(GameData.DataTotalWin, "0");
                DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
            }

            this._syncedReelIndexes = this.getSyncedReelsIndex(this._stoppedSymbolsArray);
            this._forced = false;

            let stopDelay = this._stopDelay;
            if (this._syncedReelIndexes.length > 0) {
                stopDelay = this.getStopDelay(this._syncedReelIndexes.length);

                if (force) {
                    this._forced = true;
                    this.showForceSyncedReels();
                } else {
                    this.startSyncedReelAnimation(); //show first bonus reel
                }
            }

            let stopDelayMultiplier = 0;
            if (this._reels != null) {
                for (let i: number = 0; i < this._reelCount; i++) {
                    if (this._reels[i] != null) {
                        if (this._stoppedSymbolsArray.length !== 0) {
                            this._reels[i].setData(this._stoppedSymbolsArray[i]);

                            if (this._syncedReelIndexes.indexOf(i) > -1) {
                                this._reels[i].spinStop(
                                    force,
                                    stopDelay + MachineExtended.ReelStopDelay * stopDelayMultiplier,
                                );

                                if (this._syncedReelIndexesOrdered[this._syncedReelIndexesOrdered.length - 1] == i) {
                                    stopDelayMultiplier++;
                                }
                            } else {
                                this._reels[i].spinStop(
                                    force,
                                    stopDelay + MachineExtended.ReelStopDelay * stopDelayMultiplier,
                                );
                                stopDelayMultiplier++;
                            }
                        } else {
                            if (DEBUG) {
                                this.log("ERROR no stopped symbols");
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no reels");
                }
            }
        }
    }

    /**
     * Dispatched onMachineStopped when all reels are stopped.
     * @method onReelStopped
     * @param reelIndex number - reel index
     * @return void
     */
    public onReelStopped(reelIndex: number): void {
        if (this._reels[reelIndex].isSyncedReel) {
            if (this._syncedReelIndexesOrdered[Math.round(this._syncedReelIndexesOrdered.length / 2)] == reelIndex) {
                this.finishSyncAnim();
                PlayList.play("SND_BaseRotationEndSync" + reelIndex);
            }
        } else {
            PlayList.play("SND_BaseRotationEnd" + reelIndex);
        }

        this._stoppedReelIndexes++;
        this.actualReelStopped = reelIndex;
        this._reels[reelIndex].visible = false;
        this.emit(Machine.ReelStopped);

        if (this._stoppedReelIndexes === this._reelCount) {
            PlayList.stop("SND_BaseRotation", 10);
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stopTimer.stop();

            if (DEBUG) {
                const old = AppContext.gameConfiguration.MinGameRoundDuration;
                const current = AppContext.gameConfiguration.GameRoundMinSpan;
                const minDuration = current > 0 ? current : old > 0 ? old : 0;
                const spinDuration = Date.now() - this._startSpinTime;

                if (spinDuration > minDuration || !this.verifyMinGameRoundDurationConditions()) {
                    this.log("Spin time duration: " + spinDuration + " ms");
                } else {
                    console.log("%cError spin time duration:" + spinDuration + " ms", "color: #ff9900");
                }
            }

            this.emit(Machine.MachineStopped);
            this._reelsContainer.visible = false;
        }
    }

    //******************************************************************************************************************
    //  ADDITIONAL
    //******************************************************************************************************************

    private getSyncedReelsIndex(data: Array<IReel>): Array<number> {
        this._syncedReelIndexesOrdered = [];

        for (let i = 0; i < data.length; i++) {
            if (data[i].customData && data[i].customData["isSynced"]) {
                this._syncedReelIndexesOrdered.push(i);
            }
        }

        if (this._syncedReelIndexesOrdered.length > 2) {
            return this.shuffleArray([...this._syncedReelIndexesOrdered]);
        } else {
            return this._syncedReelIndexesOrdered;
        }
    }

    private shuffleArray(toShuffle: Array<number>): Array<number> {
        let shuffled: Array<number> = [];
        let min: number;
        let max: number;
        let length = toShuffle.length - 1;

        max = Math.floor(Math.random() * toShuffle.length);
        min = max - 1;
        shuffled.push(toShuffle.splice(max, 1)[0]);

        for (let i = 0; i < length; i++) {
            if (Math.random() > 0.5) {
                if (toShuffle[max] != null) {
                    shuffled.push(toShuffle.splice(max, 1)[0]);
                } else {
                    shuffled.push(toShuffle.splice(min, 1)[0]);
                    min--;
                }
            } else {
                if (toShuffle[min] != null) {
                    shuffled.push(toShuffle.splice(min, 1)[0]);
                    min--;
                    max--;
                } else {
                    shuffled.push(toShuffle.splice(max, 1)[0]);
                }
            }
        }

        return shuffled;
    }

    private getSyncedReelStrip(): Array<number> {
        if (this._syncedReelIndexes && this._syncedReelIndexes.length > 0) {
            if (this._syncedStrips) {
                let mask = "";
                for (let i = 0; i < this._reelCount; i++) {
                    if (this._syncedReelIndexes.indexOf(i) > -1) {
                        mask = "1" + mask;
                    } else {
                        mask = "0" + mask;
                    }
                }

                return this._syncedStrips[parseInt(mask, 2)];
            } else {
                return this._reels[this._syncedReelIndexes[0]].getStrip();
            }
        }

        return [];
    }

    private showForceSyncedReels(): void {
        this._showSyncedReelTimer.stop();

        for (let i = 0; i < this._syncedReelIndexes.length; i++) {
            let reel = this._reels[this._syncedReelIndexes[i]];
            reel.setSyncedStrip(this.getSyncedReelStrip(), this._reels[this._syncedReelIndexes[0]].getStripIndex());
            reel.isSyncedReel = true;
            reel.setSyncedReelBgCase("FORCE");
        }

        PlayList.stop("SND_BaseSyncbars" + this._currentSyncedReelIndex, 15);

        let hasEndState = this._syncedLights[0].getCurrentCase() == "END";
        this._syncedLights[0].goToCase("REEL_" + this._syncedReelIndexesOrdered[0]);
        this._syncedLights[1].goToCase(
            "REEL_" + (4 - this._syncedReelIndexesOrdered[this._syncedReelIndexesOrdered.length - 1]),
        );
        this.updateReelsLight(false, "FORCE", hasEndState);

        if (this._syncedReelIndexesOrdered.length > 2) {
            this.emit(MachineExtended.SyncReelForceShowed, this._syncedReelIndexesOrdered.length - 1);
        } else {
            this.emit(MachineExtended.SyncReelForceShowed, 1);
        }
    }

    private startSyncedReelAnimation(): void {
        //show first 2 reels
        for (let i = 0; i < 2; i++) {
            let reel = this._reels[this._syncedReelIndexes[i]];
            let caseType = i == 0 ? "START_SYNC" : "START";

            reel.setSyncedStrip(this.getSyncedReelStrip(), this._reels[this._syncedReelIndexes[0]].getStripIndex());
            reel.isSyncedReel = true;
            reel.setSyncedReelBgCase(caseType);
        }

        PlayList.play("SND_BaseSyncbars2");

        //setup lights
        if (this._syncedReelIndexes[0] < this._syncedReelIndexes[1]) {
            this._syncedLights[0].goToCase("REEL_" + this._syncedReelIndexes[0]);
            this._syncedLights[1].goToCase("REEL_" + (4 - this._syncedReelIndexes[1]));
        } else {
            this._syncedLights[0].goToCase("REEL_" + this._syncedReelIndexes[1]);
            this._syncedLights[1].goToCase("REEL_" + (4 - this._syncedReelIndexes[0]));
        }

        this._currentSyncedReelIndex = 2;

        if (this._syncedReelIndexes.length > 2) {
            this._showSyncedReelTimer.start(45);
        }
    }

    private showSyncedReel(): void {
        let reelIndex = this._syncedReelIndexes[this._currentSyncedReelIndex];
        let reel = this._reels[reelIndex];
        reel.setSyncedStrip(this.getSyncedReelStrip(), this._reels[this._syncedReelIndexes[0]].getStripIndex());
        reel.isSyncedReel = true;
        reel.setSyncedReelBgCase("START");

        for (let i = 0; i < this._currentSyncedReelIndex; i++) {
            reel = this._reels[this._syncedReelIndexes[i]];
            reel.setSyncedReelBgCase("LOOP_FLASH");
        }

        this.updateReelsLight();

        //fade out previous song
        // PlayList.stop('SND_BaseSyncbars' + this._currentSyncedReelIndex, 10);
        this._currentSyncedReelIndex++;
        PlayList.play("SND_BaseSyncbars" + this._currentSyncedReelIndex);

        if (this._currentSyncedReelIndex < this._syncedReelIndexes.length) {
            //5th reel teasing
            if (
                this._currentSyncedReelIndex == 4 &&
                this._currentSyncedReelIndex == this._syncedReelIndexes.length - 1
            ) {
                this._showSyncedReelTimer.start(MachineExtended.ShowFinalSyncedReelTimerDelay);
            } else {
                this._showSyncedReelTimer.start(MachineExtended.ShowSyncedReelTimerDelay);
            }
        }

        //args count, isLeftDirection
        this.emit(
            MachineExtended.SyncReelShowed,
            this._currentSyncedReelIndex,
            reelIndex < this._syncedReelIndexes[this._currentSyncedReelIndex - 2],
        );
    }

    private updateReelsLight(change: boolean = true, caseType: string = "ANIM_", hasEndState: boolean = false): void {
        if (change) {
            let newIndex = this._syncedReelIndexes[this._currentSyncedReelIndex];
            let leftLightIndex = parseInt(this._syncedLights[0].getCurrentCase().split("_")[1]);

            if (Math.abs(newIndex - leftLightIndex) == 1) {
                this._syncedLights[0].goToCase("ANIM_" + newIndex);
            } else {
                this._syncedLights[1].goToCase("ANIM_" + (4 - newIndex));
            }
        } else {
            if (hasEndState && caseType === "FORCE") {
                caseType = "EMPTY";
            }

            this._syncedLights[0].goToCase(caseType);
            this._syncedLights[1].goToCase(caseType);
        }
    }

    private finishSyncAnim(): void {
        if (this._forced) {
            return;
        }

        PlayList.stop("SND_BaseSyncbars" + this._currentSyncedReelIndex, 15);

        for (let i = 0; i < this._syncedReelIndexes.length; i++) {
            let reel = this._reels[this._syncedReelIndexes[i]];
            reel.setSyncedStrip(this.getSyncedReelStrip(), this._reels[this._syncedReelIndexes[0]].getStripIndex());
            reel.isSyncedReel = true;
            reel.setSyncedReelBgCase("END");
        }

        this.updateReelsLight(false, "END");
    }

    //reel stop timing
    private getStopDelay(syncedReels: number): number {
        let duration = MachineExtended.ReelStopDelay; //init delay in frames

        for (let i = 3; i <= syncedReels; i++) {
            if (i == 3) {
                duration += MachineExtended.ShowSyncedReelTimerDelay + 30;
            } else if (i == 4) {
                duration += MachineExtended.ShowFinalSyncedReelTimerDelay;
            } else {
                duration += MachineExtended.ReelStopDelay + 30; //30f delayed machine stop when all 5 reels are synced
            }
        }

        return duration;
    }

    private resetSyncedReels(): void {
        if (this._reels != null) {
            for (let i = 0; i < this._reels.length; i++) {
                this._reels[i].isSyncedReel = false;
                this._reels[i].setSyncedReelBgCase("RESET");
            }
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/MachineExtended.ts
 **/